import { useCountryQuery } from "@/v2-backoffice-client/country/country.query"
import { useFeatureQuery } from "@/v2-backoffice-client/feature/feature.query"
import { FEATURE_TAB } from "@/v2-backoffice-client/feature/feature.constants"
import type { LayerFragment } from "@/v2-backoffice-client/layer/__types__/layer.fragment"
import { UNCATEGORIZED_CODE } from "@/v2-backoffice-client/layer/organize/layer.organize.constants"
import { useLayerVisibilityQuery } from "@/v2-backoffice-client/layer/visibility/layer.visibility.query"
import { FEATURE_AVAILABILITY_ALL } from "@/v2-backoffice-client/layer/visibility/layer.visibility.constants"
import {
  splitPackageNames,
  whoHasPackaged,
} from "@/v2-backoffice-client/license/changes/license.changes.utils"

export type LayerVisibilityLayer = Pick<
  LayerFragment,
  "FeatureCode" | "IsEnabled" | "CategoryCodes"
>

/**
 * One gate the console applies before it lists a layer. A blocking gate
 * hides the layer outright; the others leave it listed but unfiled or
 * unsearchable. `tab` names the sheet tab where the gate is fixed — every
 * fixable gate lives on the Availability tab, laid out in this same order.
 */
export type LayerVisibilityGate = {
  key: string
  label: string
  isOk: boolean
  isBlocking: boolean
  detail: string
  tab?: string
}

type UseLayerVisibilityGatesInput = {
  layer: LayerVisibilityLayer
}

/**
 * "Can a customer see this layer?" from the facts behind the five gates
 * api.Get_Gis_Layers applies. None of the gates logs anything when it hides
 * a layer: a layer that is published, filed and translated still vanishes
 * from every country when it has no country row (FUS-1070, and again on
 * 2026-09-08), and nothing in the sheet said so.
 *
 * Publish state and categories are read off the layer row, so they follow
 * the cache the moment the toolbar or the category chips change them; the
 * availability comes off the feature row for the same reason; the country
 * list, the search index and the carrying packages come from the runtime
 * through getLayerVisibility, refetched by the saves that move them.
 */
export function useLayerVisibilityGates(input: UseLayerVisibilityGatesInput) {
  const { layer } = input

  const { data, loading, error } = useLayerVisibilityQuery({
    variables: { featureCode: layer.FeatureCode },
  })
  const { data: featureData } = useFeatureQuery({
    variables: { featureCode: layer.FeatureCode },
  })
  const { data: countryData } = useCountryQuery()

  const visibility = data?.getLayerVisibility
  const feature = featureData?.getFeature

  if (!visibility) {
    return {
      isLoading: loading,
      error,
      hasRuntimeRow: false,
      isVisible: false,
      countryNames: [] as string[],
      gates: [] as LayerVisibilityGate[],
      blockers: [] as LayerVisibilityGate[],
      degradations: [] as LayerVisibilityGate[],
    }
  }

  const countryNames = visibility.CountryCodes.map(
    (code) =>
      countryData?.getCountries.find((country) => country.Code === code)
        ?.Name ?? String(code),
  )
  const categoryCount = layer.CategoryCodes.filter(
    (code) => code !== UNCATEGORIZED_CODE,
  ).length
  const availabilityTypeCode =
    feature?.FeatureAvailabilityTypeCode ??
    visibility.FeatureAvailabilityTypeCode
  const isAvailableToAll = availabilityTypeCode === FEATURE_AVAILABILITY_ALL
  const packageNames = splitPackageNames(visibility.PackageNames)

  const gates: LayerVisibilityGate[] = [
    {
      key: "published",
      label: "Live",
      isOk: layer.IsEnabled,
      isBlocking: true,
      detail: layer.IsEnabled ? "yes" : "no — hidden from every customer",
      tab: FEATURE_TAB.availability,
    },
    {
      key: "countries",
      label: "Countries",
      isOk: visibility.CountryCodes.length > 0,
      isBlocking: true,
      detail:
        visibility.CountryCodes.length > 0
          ? countryNames.join(", ")
          : "none — hidden in every country",
      tab: FEATURE_TAB.availability,
    },
    {
      key: "access",
      label: "Access",
      isOk: isAvailableToAll || packageNames.length > 0,
      isBlocking: true,
      detail: isAvailableToAll
        ? "everyone"
        : whoHasPackaged(true, packageNames),
      tab: FEATURE_TAB.availability,
    },
    {
      key: "categories",
      label: "Filed",
      isOk: categoryCount > 0,
      isBlocking: false,
      detail:
        categoryCount > 0
          ? `${categoryCount} categor${categoryCount === 1 ? "y" : "ies"}`
          : "no category — listed unfiled, dropped from chooser search",
      tab: FEATURE_TAB.availability,
    },
    {
      key: "search",
      label: "Searchable",
      isOk: visibility.SearchRowCount > 0,
      isBlocking: false,
      detail:
        visibility.SearchRowCount > 0
          ? "yes"
          : "not in the search index — the next catalog publish rebuilds it",
    },
  ]

  const blockers = gates.filter((gate) => gate.isBlocking && !gate.isOk)
  const degradations = gates.filter((gate) => !gate.isBlocking && !gate.isOk)

  return {
    isLoading: loading,
    error,
    hasRuntimeRow: true,
    isVisible: blockers.length === 0,
    countryNames,
    gates,
    blockers,
    degradations,
  }
}
